import { useState } from 'react';
import { Drawer, Empty, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import TeamFirmCard from '@/components/team-assignments/TeamFirmCard';
import { FUNCTIONS, personHeadcountIn, personName } from '@/components/team-assignments/model';
import { PillGroup } from './CombinedView';

/**
 * One type of work at a time, a card per person with the client entities they
 * carry. A hand-over stays on both cards once: orange on the person leaving,
 * green on the person coming. Only names here (plus the person's payroll
 * headcount); bank, method and notes are in the client card, opened in a drawer.
 */
export default function ByFunctionView({ model, fn, setFn }) {
  const { t } = useTranslation('teamAssignments');
  const { token } = theme.useToken();
  const [open, setOpen] = useState(null);
  const { groups, people } = model;
  const current = fn || 'PAYROLL';

  const withFn = groups.filter((g) => g.rows.some((a) => a.function === current));
  const cards = people
    .map((p) => {
      const mineOf = (g) => g.rows.filter((a) => a.employee === p._id && a.function === current);
      // Leads, incoming and outgoing together, each entity once.
      const lead = withFn
        .map((g) => ({ g, rows: mineOf(g).filter((a) => a.role === 'LEAD') }))
        .filter((x) => x.rows.length)
        .map(({ g, rows }) => ({
          g,
          state: rows.some((a) => !a.transition) ? null : rows.some((a) => a.transition === 'IN') ? 'IN' : 'OUT',
        }));
      const help = withFn.filter((g) => mineOf(g).some((a) => a.role === 'HELPER') && !lead.some((x) => x.g.key === g.key));
      const headcount = current === 'PAYROLL' ? lead.reduce((s, x) => s + (personHeadcountIn(x.g, p._id) || 0), 0) : 0;
      return { p, lead, help, headcount };
    })
    .filter((x) => x.lead.length || x.help.length)
    .sort((a, b) => b.lead.length - a.lead.length || personName(a.p).localeCompare(personName(b.p), 'sr'));

  const stateStyle = (state) =>
    state === 'OUT'
      ? { color: token.colorWarningText, borderInlineStartColor: token.colorWarning }
      : state === 'IN'
        ? { color: token.colorSuccessText, borderInlineStartColor: token.colorSuccess }
        : undefined;

  const firmLine = (g, state, personId) => {
    const hc = current === 'PAYROLL' && personId ? personHeadcountIn(g, personId) : null;
    return (
      <li
        key={g.key}
        // A thin line under every client, so a long name that wraps still reads as one firm.
        className="flex items-baseline justify-between gap-2 border-b border-l-2 border-zinc-100 border-l-zinc-200 py-1 pl-2 last:border-b-0"
        style={stateStyle(state)}
      >
        <button type="button" className="text-left hover:underline" onClick={() => setOpen(g)}>
          {g.name}
          {g.size > 1 ? <span className="opacity-50"> (+{g.size - 1})</span> : null}
          {state === 'OUT' ? ' →' : state === 'IN' ? ' ←' : ''}
        </button>
        {hc != null && <span className="shrink-0 text-right font-mono text-[11.5px] opacity-60">{hc}</span>}
      </li>
    );
  };

  return (
    <div className="grid gap-3">
      <PillGroup
        items={FUNCTIONS.map((f) => ({ key: f, label: t(`functions.${f}`), count: groups.filter((g) => g.rows.some((a) => a.function === f)).length }))}
        value={current}
        onChange={setFn}
        label={t('fnPick.label')}
      />
      <div className="-mt-1 flex flex-wrap gap-x-3 text-xs opacity-80">
        <span>
          {t('fnPick.summary', { fn: t(`functions.${current}`), firms: withFn.length, people: cards.length })}
          {current === 'PAYROLL' ? ` · ${t('people.employees', { count: withFn.reduce((s, g) => s + (g.headcount || 0), 0) })}` : ''}
        </span>
        <span style={{ color: token.colorWarningText }}>{t('people.legendOut')}</span>
        <span style={{ color: token.colorSuccessText }}>{t('people.legendIn')}</span>
      </div>
      {cards.length ? (
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
          {cards.map((c) => {
            const outs = c.lead.filter((x) => x.state === 'OUT').length;
            const ins = c.lead.filter((x) => x.state === 'IN').length;
            return (
              <article key={c.p._id} className="flex min-w-0 flex-col gap-2 rounded-xl border border-zinc-200 p-3.5">
                <div>
                  <h3 className="m-0 text-base font-semibold">{personName(c.p)}</h3>
                  <div className="text-xs opacity-70">
                    {[
                      t('people.firms', { count: c.lead.length }),
                      c.headcount ? t('people.employees', { count: c.headcount }) : null,
                      c.help.length ? t('people.helpsOn', { count: c.help.length }) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    {outs > 0 && <span style={{ color: token.colorWarningText }}> · {t('people.outgoing', { count: outs })}</span>}
                    {ins > 0 && <span style={{ color: token.colorSuccessText }}> · {t('people.incoming', { count: ins })}</span>}
                  </div>
                </div>
                <ul className="m-0 grid list-none p-0 text-[13px]">{c.lead.map((x) => firmLine(x.g, x.state, c.p._id))}</ul>
                {c.help.length > 0 && (
                  <>
                    <div className="text-xs font-semibold opacity-60">{t('people.helps')}</div>
                    <ul className="m-0 grid list-none p-0 text-[13px] opacity-80">{c.help.map((g) => firmLine(g, null, null))}</ul>
                  </>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <Empty description={t('table.empty')} />
      )}
      <Drawer open={!!open} width={760} title={open?.name} onClose={() => setOpen(null)} destroyOnClose>
        {open && <TeamFirmCard companyId={open.primaryId} embedded showCompanyLink />}
      </Drawer>
    </div>
  );
}
